import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, createFilterOptions } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faArrowRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { User } from "../../../core/models/entities/User";
import { useApiClient } from "../../../data/clients/ApiClient";
import { CLARITY_MASK } from "../../../utils/analytics/clarityAttrs";

interface UserSearchAutoCompleteProps {
  value: User | null;
  onChange: (user: User | null) => void;
  organizationId: number;
  disabled?: boolean;
  placeholder?: string;
}

export function UserSearchAutoComplete({
  value,
  onChange,
  organizationId,
  disabled = false,
  placeholder,
}: UserSearchAutoCompleteProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder =
    placeholder ?? t("compDialogsInput.searchForUser");
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const apiClient = useApiClient();

  useEffect(() => {
    const fetchAllUsers = async () => {
      if (!organizationId || hasLoaded) return;

      setLoading(true);
      try {
        const users = await apiClient.getUsersOfOrganization(organizationId);
        setAllUsers(users);
        setFilteredUsers(users);
        setHasLoaded(true);
      } catch (error) {
        console.error('Error fetching users:', error);
        setAllUsers([]);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, [organizationId, apiClient, hasLoaded]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(allUsers);
      return;
    }

    const filtered = allUsers.filter(user => 
      user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchQuery, allUsers]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    setSearchQuery(newValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  // Sync inputValue with the selected value
  useEffect(() => {
    if (value) {
      setInputValue(`${value.firstName} ${value.lastName} (${value.email})`);
    } else if (!value && inputValue) {
      setInputValue("");
    }
  }, [value]);

  const filterOptions = createFilterOptions<User>({
    limit: 200,
  });

  return (
    <Autocomplete
      disabled={disabled}
      className={"!text-xs"}
      disablePortal
      options={filteredUsers}
      value={value}
      loading={loading && !hasLoaded}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        `${option.firstName} ${option.lastName} (${option.email})`
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => {
        // @ts-ignore
        const { key, ...noKeyProps } = props;
        return (
          <Box key={option.id} component="li" {...noKeyProps}>
            {/* Clarity masking: a person's name and e-mail in a MUI listbox
                (not a native select) - same as CustomAutoComplete. */}
            <div className={"flex text-xs items-center space-x-2"} {...CLARITY_MASK}>
              <p>
                {option.firstName} {option.lastName}
              </p>
              <span className="text-gray-500">({option.email})</span>
            </div>
          </Box>
        );
      }}
      renderInput={(params) => (
        <div
          ref={params.InputProps.ref}
          className="border rounded-md border-gray-200 border-solid h-9 flex space-x-2 items-center pl-2 bg-white focus-within:border-gray-600 relative"
        >
          <span>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
          <input
            {...params.inputProps}
            className="border-none text-xs font-medium outline-none w-full pr-8"
            placeholder={resolvedPlaceholder}
            value={inputValue}
            onChange={(e) => {
              handleInputChange(e.target.value);
              // Call the original onChange to maintain MUI Autocomplete functionality
              if (params.inputProps.onChange) {
                params.inputProps.onChange(e);
              }
            }}
            onKeyPress={handleKeyPress}
          />
          {/* Loading spinner */}
          {loading && !hasLoaded && (
            <div className="absolute right-2 p-1">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-gray-500"
              />
            </div>
          )}
        </div>
      )}
      onChange={(x, v) => onChange(v)}
    />
  );
}
